/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The author-page URL contract, in one place.
 *
 * The slug of an author is derived from their admin username. Until now two
 * copies of that derivation existed (server/routes/profiles.ts and
 * scripts/prerender.mjs) and agreed only by discipline; now that the slug is
 * a public URL (/<lang>/auteur/<slug>) with a baked file, a sitemap entry, a
 * Person node and article links pointing at it, a divergence would be a 404
 * on a page we ourselves advertise. Plain JS so both of those consumers and
 * the client import the same function.
 */

/**
 * The route segment. Dutch under /en too, like every other segment of this
 * site (/en/zakelijk, /en/begrippen) — the language lives in the prefix.
 */
export const AUTHOR_SEGMENT = 'auteur';

/**
 * Lowercase, diacritics stripped, anything that is not a-z/0-9 collapsed to a
 * single hyphen, no leading/trailing hyphen. `Jasper` → `jasper`,
 * `José van Dijk` → `jose-van-dijk`.
 *
 * @param {unknown} username
 * @returns {string} '' for anything that is not a usable username.
 */
export function authorSlug(username) {
  if (typeof username !== 'string') return '';
  return username
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {'nl'|'en'} lang
 * @param {string} slug  Already-derived slug (see {@link authorSlug}).
 */
export function authorPath(lang, slug) {
  return `/${lang}/${AUTHOR_SEGMENT}/${slug}`;
}

/**
 * The PUBLISHED articles credited to a username (case-insensitive, the same
 * match lookupPublicAuthor makes). One function for the author page's list,
 * the sitemap's "has articles" gate and the bake's thin-page count, so the
 * three can never disagree on who wrote what.
 *
 * @param {any[]} articles  Raw CMS records.
 * @param {unknown} username
 * @returns {any[]}
 */
export function articlesByAuthor(articles, username) {
  if (typeof username !== 'string' || !username || !Array.isArray(articles)) return [];
  const target = username.toLowerCase();
  return articles.filter(
    (a) =>
      a?.published === true &&
      typeof a.authorUsername === 'string' &&
      a.authorUsername.toLowerCase() === target
  );
}
